import { parse } from 'date-fns';

import { SENTIMENT_MIN_START_MONTH } from '@revelio/core/global-ai-assistant/sentiment-window';
import type {
  QuerySentimentV3QueryVariables,
  SentimentV3Ent,
  SentimentV3Filters,
} from '@revelio/data-access';

import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../types/filters';
import { getNumericIdFromNestedId } from './get-numeric-id-from-nested-id';
import { getV3CompanyFilters } from './get-v3-company-filters';
import { getV3Entities } from './get-v3-entities';
import { getV3GeographyFilters } from './get-v3-geography-filters';
import { getV3ListFilter } from './get-v3-list-filter';
import { getV3Taxonomies } from './get-v3-taxonomies';
import { clampDates, getDateFromFilters } from './getDateFilter';
import { mergeRicsFilterKeys } from './merge-rics-filter-keys';
import { appendRoleSelectionToFilters } from './role-skill-ent-kind';

/**
 * Parsed from the SHARED constant, not a literal: the assistant's server-side
 * sentiment read floors at the same month and is documented to match this one.
 * A literal here could drift from it with nothing failing.
 */
const minSentimentStartDate = parse(
  SENTIMENT_MIN_START_MONTH,
  'yyyy-MM',
  new Date()
);

const getSentimentV3Filters = ({
  primaryDimension,
  filters,
  isSnapshot,
}: {
  primaryDimension: PrimaryFilterId;
  filters: Filter[];
  isSnapshot: boolean;
}): SentimentV3Filters => {
  const sentimentFilters = filters.reduce<SentimentV3Filters>(
    (acc, filter): SentimentV3Filters => {
      if (filter.id === primaryDimension) {
        return acc;
      }
      switch (filter.id) {
        case FilterId.COMPANY:
        case FilterId.INDUSTRY:
          return mergeRicsFilterKeys(acc, getV3CompanyFilters(filter));
        case FilterId.GEOGRAPHY:
          return { ...acc, ...getV3GeographyFilters(filter) };

        case FilterId.ROLE: {
          if (filter.type !== FilterType.TREE) return acc;
          return filter.selections.reduce<SentimentV3Filters>(
            (inner, selection) => {
              const id = getNumericIdFromNestedId(String(selection.id));
              if (id === null) return inner;
              return {
                ...inner,
                ...appendRoleSelectionToFilters(
                  inner,
                  String(selection.levelId),
                  id
                ),
              };
            },
            acc
          );
        }

        case FilterId.SENIORITY:
          return { ...acc, ...getV3ListFilter({ filter, key: 'seniority' }) };

        case FilterId.OVERALL_RATING: {
          if (filter.type !== FilterType.STATIC_LIST) return acc;
          return {
            ...acc,
            overall: filter.selections.map((selection) =>
              parseInt(selection.id, 10)
            ),
          };
        }

        default:
          return acc;
      }
    },
    {}
  );

  const dateFilter = getDateFromFilters({
    filters,
    granularity: 'month',
    isSnapshot,
  });

  if (isSnapshot) {
    if (dateFilter === null) {
      return sentimentFilters;
    }
    return {
      ...sentimentFilters,
      start_date: dateFilter.start_date,
      end_date: dateFilter.end_date,
    };
  }

  return {
    ...sentimentFilters,
    ...clampDates({
      start_date: dateFilter?.start_date,
      end_date: dateFilter?.end_date,
      minStartDate: minSentimentStartDate,
      dateGranularity: 'month',
    }),
  };
};

export const getSentimentV3Variables = ({
  filters,
  primaryDimension,
  isSnapshot,
}: {
  filters: Filter[];
  primaryDimension: PrimaryFilterId;
  isSnapshot: boolean;
}): QuerySentimentV3QueryVariables | null => {
  const entities = getV3Entities({ primaryDimension, filters });

  if (entities.length === 0) {
    return null;
  }

  return buildSentimentV3VariablesFromEntities({
    entities,
    filters,
    primaryDimension,
    isSnapshot,
  });
};

export const buildSentimentV3VariablesFromEntities = ({
  entities,
  filters,
  primaryDimension,
  isSnapshot,
}: {
  entities: SentimentV3Ent[];
  filters: Filter[];
  primaryDimension: PrimaryFilterId;
  isSnapshot: boolean;
}): QuerySentimentV3QueryVariables => {
  const sentimentFilters = getSentimentV3Filters({
    filters,
    primaryDimension,
    isSnapshot,
  });

  const { role_taxonomy, skill_taxonomy } = getV3Taxonomies(filters);

  return {
    req: {
      entities,
      filters: sentimentFilters,
      role_taxonomy,
      skill_taxonomy,
    },
  };
};
